import { FC, createContext, useContext, useReducer } from 'react'
import { LEVELS } from 'src/components/common/Menu/constants'
import {
  MenuState,
  MenuStateAction,
} from 'src/components/common/Menu/global-navigation'

type Context = {
  state: MenuState
  dispatch: (object: MenuStateAction) => void
  reset: () => void
  handleDispatch: (
    type: string,
    index: number,
    label: string,
    nextLevelValue: number
  ) => void
}

const MenuContext = createContext<Context>({} as Context)

const reset = () => {
  return { index: null, label: null }
}

const reducer = (state: MenuState, action: MenuStateAction): MenuState => {
  switch (action.type) {
    case 'go_back': {
      return { ...state, selectedLevel: state.selectedLevel - 1 }
    }
    case '1': {
      return {
        ...state,
        firstLevelItem: {
          index: action?.value?.index,
          label: action?.value?.label,
        },
        secondLevelItem: reset(),
        thirdLevelItem: reset(),
        fourthLevelItem: reset(),
      }
    }
    case '2': {
      return {
        ...state,
        secondLevelItem: {
          index: action?.value?.index,
          label: action?.value?.label,
        },
        thirdLevelItem: reset(),
        fourthLevelItem: reset(),
      }
    }
    case '3': {
      return {
        ...state,
        thirdLevelItem: {
          index: action?.value?.index,
          label: action?.value?.label,
        },
        fourthLevelItem: reset(),
      }
    }
    case '4': {
      return {
        ...state,
        fourthLevelItem: {
          index: action?.value?.index,
          label: action?.value?.label,
        },
      }
    }
    case 'selected_level': {
      return {
        ...state,
        selectedLevel:
          typeof action.value === 'number' ? action.value : state.selectedLevel,
      }
    }
    case 'reset': {
      return {
        selectedLevel: LEVELS.main,
        firstLevelItem: reset(),
        secondLevelItem: reset(),
        thirdLevelItem: reset(),
        fourthLevelItem: reset(),
      }
    }
    default:
      return {
        selectedLevel: LEVELS.main,
        firstLevelItem: reset(),
        secondLevelItem: reset(),
        thirdLevelItem: reset(),
        fourthLevelItem: reset(),
      }
  }
}

export const MenuProvider: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(reducer, {
    selectedLevel: LEVELS.main,
    firstLevelItem: reset(),
    secondLevelItem: reset(),
    thirdLevelItem: reset(),
    fourthLevelItem: reset(),
  })

  const handleDispatch = (
    type: string,
    index: number,
    label: string,
    nextLevelValue: number
  ) => {
    dispatch({ type, value: { index, label } })
    dispatch({ type: 'selected_level', value: nextLevelValue })
  }

  const value = {
    state,
    dispatch,
    reset,
    handleDispatch,
  }

  return <MenuContext.Provider value={value}>{children}</MenuContext.Provider>
}

export const useMenu = () => {
  return useContext(MenuContext)
}
